import React, { useState, useEffect, useCallback } from 'react';
import { HeroImages } from '../../constants';
import { useStateContext } from '../../context/ContextProvider';
import Button from '../layout/Button';
import { useTranslation } from "react-i18next";
import { 
  FaChevronLeft, 
  FaChevronRight, 
  FaPlay, 
  FaPause,
  FaArrowRight
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

function Slider() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const { currentDir } = useStateContext();
  const { t } = useTranslation();

  useEffect(() => {
    let interval;
    if (isAutoPlaying && !isHovering) {
      interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % HeroImages.length);
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAutoPlaying, isHovering]);

  const goToSlide = useCallback((index) => {
    if (index >= HeroImages.length) {
      index = 0;
    } else if (index < 0) {
      index = HeroImages.length - 1;
    }
    setCurrentIndex(index);
  }, []);

  const nextSlide = useCallback(() => {
    goToSlide(currentIndex + 1);
  }, [currentIndex, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide(currentIndex - 1);
  }, [currentIndex, goToSlide]);

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  return (
    <div
      className="relative w-full h-screen overflow-hidden bg-black"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Background */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, scale: 1.15 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.1 }}
          transition={{ duration: 1 }}
          className="absolute inset-0"
        >
          <img
            src={HeroImages[currentIndex].img}
            alt=""
            className="w-full h-full object-cover"
          />
  
          {/* Cinematic Overlay */}
          <div className="absolute inset-0 dark:bg-black/60 " />
  
          {/* Gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-color-2/40 via-color-2/20 dark:from-black/80 dark:via-black/40 to-transparent" />
  
          {/* Animated Blur Circle */}
          <div className="absolute top-20 left-20 w-72 h-72 bg-color-1/10 blur-3xl rounded-full animate-pulse" />
        </motion.div>
      </AnimatePresence>
  
      {/* Main Content */}
      <div className="relative z-20 h-full flex items-center">
        <div className="container mx-auto px-6 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            {/* Glass Card */}
            <div
              className="backdrop-blur-xl bg-white/10 border border-white/10 
              rounded-3xl p-8 md:p-12 shadow-2xl"
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full 
                bg-color-2/20 border border-color-2/30 mb-6"
              >
                <div className="w-2 h-2 bg-color-2 rounded-full animate-ping" />
  
                <span className="text-color-2 font-kufi text-sm md:text-base">
                  {t("Saba'a For Mandy")}
                </span>
              </motion.div>
  
              {/* Title */}
              <motion.h1
                initial={{
                  opacity: 0,
                  x: currentDir === "rtl" ? 50 : -50,
                }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
                className="text-4xl md:text-6xl xl:text-7xl font-bold 
                text-white leading-tight mb-6 font-kufi"
                style={{
                  textShadow: "0 5px 20px rgba(0,0,0,0.4)",
                }}
              >
                {currentDir === "ltr"
                  ? HeroImages[currentIndex].titleEn
                  : HeroImages[currentIndex].titleAr}
              </motion.h1>
  
              {/* Subtitle */}
              <motion.p
                initial={{
                  opacity: 0,
                  x: currentDir === "rtl" ? 50 : -50,
                }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="text-white/80 text-lg md:text-xl leading-relaxed 
                mb-8 max-w-2xl font-kufi"
              >
                {currentDir === "ltr"
                  ? HeroImages[currentIndex].subTitleEn
                  : HeroImages[currentIndex].subTitleAr}
              </motion.p>
  
              {/* Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="flex flex-wrap gap-4"
              >
                <Link to={"/MealsCategories"}>
                  <button
                    className="group px-8 py-4 rounded-2xl bg-color-1 text-white 
                    font-semibold flex items-center gap-3 hover:scale-105 
                    transition-all duration-300 shadow-xl"
                  >
                    {t("explore categories")}
  
                    <FaArrowRight
                      className={`transition-transform duration-300 group-hover:translate-x-1 ${
                        currentDir === "rtl" ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </Link>
  
                <button
                  onClick={toggleAutoPlay}
                  className="w-14 h-14 rounded-2xl border border-white/20 
                  bg-white/10 backdrop-blur-md text-white flex items-center 
                  justify-center hover:bg-white/20 transition-all duration-300"
                >
                  {isAutoPlaying ? <FaPause /> : <FaPlay />}
                </button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
  
      {/* Navigation */}
      <button
        onClick={prevSlide}
        className={`absolute top-1/2 -translate-y-1/2 z-30 hidden md:flex 
        items-center justify-center w-14 h-14 rounded-full 
        bg-white/10 backdrop-blur-lg border border-white/20 
        text-white hover:scale-110 transition-all duration-300
        ${
          currentDir === "rtl"
            ? "right-8"
            : "left-8"
        }`}
      >
        {currentDir === "rtl" ? (
          <FaChevronRight />
        ) : (
          <FaChevronLeft />
        )}
      </button>
  
      <button
        onClick={nextSlide}
        className={`absolute top-1/2 -translate-y-1/2 z-30 hidden md:flex 
        items-center justify-center w-14 h-14 rounded-full 
        bg-white/10 backdrop-blur-lg border border-white/20 
        text-white hover:scale-110 transition-all duration-300
        ${
          currentDir === "rtl"
            ? "left-8"
            : "right-8"
        }`}
      >
        {currentDir === "rtl" ? (
          <FaChevronLeft />
        ) : (
          <FaChevronRight />
        )}
      </button>
  
      {/* Indicators */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-30 flex gap-3">
        {HeroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 rounded-full ${
              currentIndex === index
                ? "w-12 h-3 bg-color-1"
                : "w-3 h-3 bg-white/40 hover:bg-white"
            }`}
          />
        ))}
      </div>
  
      {/* Counter */}
      <div
        className={`absolute bottom-10 z-30 text-white 
        bg-white/10 backdrop-blur-md px-4 py-2 rounded-full
        ${
          currentDir === "rtl"
            ? "left-8"
            : "right-8"
        }`}
      >
        <span className="text-color-1 font-bold">
          {currentIndex + 1}
        </span>
        <span className="mx-1">/</span>
        <span>{HeroImages.length}</span>
      </div>
    </div>
  );
}
export default Slider;